import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Layout (her protected sayfa için gerekli — eager)
import Layout from './components/Layout';

// Login eager — login sayfası ana giriş ve küçük
import Login from './pages/Login';

// Diğer sayfalar lazy — ana bundle küçülür, public ziyaretçi indirmek zorunda kalmaz
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Devices = lazy(() => import('./pages/Devices'));
const DeviceForm = lazy(() => import('./pages/DeviceForm'));
const DeviceDetail = lazy(() => import('./pages/DeviceDetail'));
const Maintenances = lazy(() => import('./pages/Maintenances'));
const MaintenanceForm = lazy(() => import('./pages/MaintenanceForm'));
const PeriodicControls = lazy(() => import('./pages/PeriodicControls'));
const PeriodicControlForm = lazy(() => import('./pages/PeriodicControlForm'));
const FaultReports = lazy(() => import('./pages/FaultReports'));
const FaultReportForm = lazy(() => import('./pages/FaultReportForm'));
const SpareParts = lazy(() => import('./pages/SpareParts'));
const SparePartForm = lazy(() => import('./pages/SparePartForm'));
const SparePartDetail = lazy(() => import('./pages/SparePartDetail'));
const SparePartStock = lazy(() => import('./pages/SparePartStock'));
const QRScanner = lazy(() => import('./pages/QRScanner'));
const Reports = lazy(() => import('./pages/Reports'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const PurchaseRequests = lazy(() => import('./pages/PurchaseRequests'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const CalibrationList = lazy(() => import('./pages/CalibrationList'));
const CalibrationForm = lazy(() => import('./pages/CalibrationForm'));
const CalibrationDetail = lazy(() => import('./pages/CalibrationDetail'));
const MaintenanceApprovals = lazy(() => import('./pages/MaintenanceApprovals'));
const MyMaintenances = lazy(() => import('./pages/MyMaintenances'));
const Cranes = lazy(() => import('./pages/Cranes'));
const CraneDetail = lazy(() => import('./pages/CraneDetail'));
const Caddies = lazy(() => import('./pages/Caddies'));
const WeldingMachines = lazy(() => import('./pages/WeldingMachines'));

// Settings — admin only
const Users = lazy(() => import('./pages/settings/Users'));
const Departments = lazy(() => import('./pages/settings/Departments'));
const Categories = lazy(() => import('./pages/settings/Categories'));
const Checklists = lazy(() => import('./pages/settings/Checklists'));
const DeletionLogs = lazy(() => import('./pages/DeletionLogs'));

// Public sayfalar (login gerektirmez)
const CalibrationKunye = lazy(() => import('./pages/CalibrationKunye'));
const DeviceCard = lazy(() => import('./pages/DeviceCard'));

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    const here = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(here)}`} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user) {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    const target = (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) ? redirect : '/';
    return <Navigate to={target} replace />;
  }

  return children;
};

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Devices */}
      <Route
        path="/devices"
        element={
          <ProtectedRoute>
            <Devices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devices/new"
        element={
          <ProtectedRoute adminOnly>
            <DeviceForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devices/:id"
        element={
          <ProtectedRoute>
            <DeviceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devices/:id/edit"
        element={
          <ProtectedRoute adminOnly>
            <DeviceForm />
          </ProtectedRoute>
        }
      />

      {/* Maintenances */}
      <Route
        path="/maintenances"
        element={
          <ProtectedRoute>
            <Maintenances />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenances/new"
        element={
          <ProtectedRoute>
            <MaintenanceForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenances/:id"
        element={
          <ProtectedRoute>
            <MaintenanceForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance-approvals"
        element={
          <ProtectedRoute>
            <MaintenanceApprovals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-maintenances"
        element={
          <ProtectedRoute>
            <MyMaintenances />
          </ProtectedRoute>
        }
      />

      {/* Periodic Controls */}
      <Route
        path="/periodic-controls"
        element={
          <ProtectedRoute>
            <PeriodicControls />
          </ProtectedRoute>
        }
      />
      <Route
        path="/periodic-controls/new"
        element={
          <ProtectedRoute>
            <PeriodicControlForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/periodic-controls/:id"
        element={
          <ProtectedRoute>
            <PeriodicControlForm />
          </ProtectedRoute>
        }
      />

      {/* Fault Reports */}
      <Route
        path="/fault-reports"
        element={
          <ProtectedRoute>
            <FaultReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fault-reports/new"
        element={
          <ProtectedRoute>
            <FaultReportForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fault-reports/:id"
        element={
          <ProtectedRoute>
            <FaultReportForm />
          </ProtectedRoute>
        }
      />

      {/* QR Scanner */}
      <Route
        path="/qr-scanner"
        element={
          <ProtectedRoute>
            <QRScanner />
          </ProtectedRoute>
        }
      />

      {/* Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        }
      />

      {/* Spare Parts */}
      <Route
        path="/spare-parts"
        element={
          <ProtectedRoute>
            <SpareParts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spare-parts/new"
        element={
          <ProtectedRoute adminOnly>
            <SparePartForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spare-parts/:id"
        element={
          <ProtectedRoute>
            <SparePartDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spare-parts/:id/edit"
        element={
          <ProtectedRoute adminOnly>
            <SparePartForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spare-parts/:id/stock"
        element={
          <ProtectedRoute>
            <SparePartStock />
          </ProtectedRoute>
        }
      />

      {/* Purchasing */}
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            <Suppliers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-requests"
        element={
          <ProtectedRoute>
            <PurchaseRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-orders"
        element={
          <ProtectedRoute>
            <PurchaseOrders />
          </ProtectedRoute>
        }
      />

      {/* Cranes (Vinçler) */}
      <Route path="/cranes" element={<ProtectedRoute><Cranes /></ProtectedRoute>} />
      <Route path="/cranes/:id" element={<ProtectedRoute><CraneDetail /></ProtectedRoute>} />
      <Route path="/caddies" element={<ProtectedRoute><Caddies /></ProtectedRoute>} />
      <Route path="/welding-machines" element={<ProtectedRoute><WeldingMachines /></ProtectedRoute>} />

      {/* Calibration */}
      <Route
        path="/calibration"
        element={
          <ProtectedRoute>
            <CalibrationList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calibration/new"
        element={
          <ProtectedRoute adminOnly>
            <CalibrationForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calibration/:id"
        element={
          <ProtectedRoute>
            <CalibrationDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calibration/:id/edit"
        element={
          <ProtectedRoute adminOnly>
            <CalibrationForm />
          </ProtectedRoute>
        }
      />

      {/* Settings - Admin Only */}
      <Route
        path="/settings/users"
        element={
          <ProtectedRoute adminOnly>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/departments"
        element={
          <ProtectedRoute adminOnly>
            <Departments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/categories"
        element={
          <ProtectedRoute adminOnly>
            <Categories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/checklists"
        element={
          <ProtectedRoute adminOnly>
            <Checklists />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deletion-logs"
        element={
          <ProtectedRoute>
            <DeletionLogs />
          </ProtectedRoute>
        }
      />

      {/* Public - Kalibrasyon Kunye (login gerektirmez) */}
      <Route path="/kalibrasyon-kunye/:id" element={<CalibrationKunye />} />

      {/* Public - Cihaz Kartı (QR ile açılır, login gerektirmez) */}
      <Route path="/k/:id" element={<DeviceCard />} />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3500,
            style: {
              background: '#fff',
              color: '#1f2937',
              borderRadius: '12px',
              padding: '12px 16px',
              boxShadow: '0 10px 25px -8px rgba(15, 23, 42, 0.15), 0 4px 12px -4px rgba(15, 23, 42, 0.08)',
              border: '1px solid #e5e7eb',
              fontSize: '14px',
              fontWeight: 500,
            },
            success: {
              style: { borderLeft: '4px solid #22c55e' },
              iconTheme: { primary: '#22c55e', secondary: '#fff' },
            },
            error: {
              style: { borderLeft: '4px solid #ef4444' },
              iconTheme: { primary: '#ef4444', secondary: '#fff' },
            },
            loading: {
              style: { borderLeft: '4px solid #0ea5e9' },
            },
          }}
        />
      </AuthProvider>
    </Router>
  );
}

export default App;
